import type { Listing } from '@viet-parenting/supabase/types';
import { pick, t } from '../i18n';
import { getLanguageByCode, languageName } from '../languages';
import PhotoCarousel from './PhotoCarousel';
import { BackLink, Pill, ReportLink, SectionLabel, StickyCta, SummaryRow } from './parts';
import {
  ChatIcon,
  ClockIcon,
  ExternalLinkIcon,
  LinesIcon,
  MailIcon,
  PhoneIcon,
  PinIcon,
  TableIcon,
} from './icons';
import type { DetailContext } from './types';

interface Props {
  listing: Listing;
  ctx: DetailContext;
}

export default function ClassroomDetail({ listing, ctx }: Props) {
  const { locale } = ctx;
  const name = pick(locale, listing.name_vi, listing.name_en);
  const intro = pick(locale, listing.short_intro_vi ?? '', listing.short_intro_en);
  const classTime = pick(locale, listing.class_time_vi ?? '', listing.class_time_en);
  const highlights = pick(locale, listing.highlights_vi ?? '', listing.highlights_en);

  const updatedLabel = new Date(listing.updated_at).toLocaleDateString(
    locale === 'vi' ? 'vi-VN' : 'en-US',
    { month: 'long', year: 'numeric' },
  );

  const hasOnline = listing.formats.includes('online');
  const hasOffline = listing.formats.includes('offline');
  const photos = listing.photos ?? [];
  const contacts = <ContactButtons listing={listing} ctx={ctx} />;
  const stickyContacts = <ContactButtons listing={listing} ctx={ctx} compact />;
  const hasContacts = Boolean(
    listing.website_url || listing.contact_email || listing.contact_phone || listing.social_media_url,
  );

  return (
    <>
      <div className="vp-page">
        <BackLink ctx={ctx} label={locale === 'vi' ? 'Lớp học' : 'Classrooms'} />

        <div className="vp-listing-header">
          <h1 className="vp-listing-name" data-pagefind-meta="title">
            {name}
          </h1>
          <div className="vp-header-badges">
            <span className="vp-location-badge">
              <PinIcon />
              {listing.venue_city}
              {listing.venue_country ? `, ${listing.venue_country}` : ''}
            </span>
            {hasOnline && (
              <span className="vp-location-badge vp-badge-pink">
                <ClockIcon />
                {hasOffline
                  ? locale === 'vi'
                    ? 'Cũng có online'
                    : 'Also online'
                  : 'Online'}
              </span>
            )}
          </div>
          {listing.venue_address && <p className="vp-listing-address">{listing.venue_address}</p>}
        </div>

        <div className="vp-detail-grid">
          <PhotoCarousel photos={photos} name={name} ctx={ctx} />

          {(intro || highlights) && (
            <div className="vp-card vp-card-about">
              <SectionLabel icon={<LinesIcon />}>
                {locale === 'vi' ? 'Giới thiệu' : 'About'}
              </SectionLabel>
              {intro && <div className="vp-rich" dangerouslySetInnerHTML={{ __html: intro }} />}
              {highlights && (
                <>
                  <h3 className="vp-subhead">{locale === 'vi' ? 'Highlight' : 'Highlights'}</h3>
                  <p className="vp-description">{highlights}</p>
                </>
              )}
            </div>
          )}

          <div className="vp-card vp-card-summary">
            <div className="vp-cta-row" data-vp-cta-row>
              {contacts}
            </div>

            <SectionLabel icon={<TableIcon />}>
              {locale === 'vi' ? 'Thông tin nhanh' : 'Quick Summary'}
            </SectionLabel>

            <table className="vp-summary-table">
              <tbody>
                {listing.age_groups.length > 0 && (
                  <SummaryRow label={locale === 'vi' ? 'Độ tuổi' : 'Age group'}>
                    {listing.age_groups.map((a) => (
                      <Pill tone="pink" key={a}>
                        {t(locale, a)}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
                {listing.dialects.length > 0 && (
                  <SummaryRow label={locale === 'vi' ? 'Phương ngữ' : 'Teacher accent'}>
                    {listing.dialects.map((d) => (
                      <Pill tone="yellow" key={d}>
                        {t(locale, d)}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
                {listing.school_model && (
                  <SummaryRow label={locale === 'vi' ? 'Mô hình' : 'Model'}>
                    <Pill tone="mint">{t(locale, listing.school_model)}</Pill>
                  </SummaryRow>
                )}
                {listing.tuition_type && (
                  <SummaryRow label={locale === 'vi' ? 'Học phí' : 'Tuition'}>
                    <Pill tone={listing.tuition_type === 'free' ? 'mint' : 'pink'}>
                      {t(locale, listing.tuition_type)}
                    </Pill>
                  </SummaryRow>
                )}
                {listing.curriculum_type && (
                  <SummaryRow label={locale === 'vi' ? 'Giáo trình' : 'Curriculum'}>
                    <Pill tone="mint">{t(locale, listing.curriculum_type)}</Pill>
                  </SummaryRow>
                )}
                {listing.formats.length > 0 && (
                  <SummaryRow label={locale === 'vi' ? 'Hình thức học' : 'Format'}>
                    {listing.formats.map((f) => (
                      <Pill tone={f === 'online' ? 'blue' : 'yellow'} key={f}>
                        {t(locale, f)}
                      </Pill>
                    ))}
                  </SummaryRow>
                )}
                {listing.year_started && (
                  <SummaryRow label={locale === 'vi' ? 'Bắt đầu từ' : 'Open since'}>
                    <Pill tone="blue">{listing.year_started}</Pill>
                  </SummaryRow>
                )}
                {listing.support_languages.length > 0 && (
                  <SummaryRow label={locale === 'vi' ? 'Ngôn ngữ hỗ trợ' : 'Assisted language'}>
                    {listing.support_languages.map((code) => {
                      const lang = getLanguageByCode(code);
                      return (
                        <Pill tone="mint" key={code}>
                          {lang ? languageName(lang, locale) : code}
                        </Pill>
                      );
                    })}
                  </SummaryRow>
                )}
                {classTime && (
                  <SummaryRow label={locale === 'vi' ? 'Giờ học' : 'Class time'}>
                    {classTime}
                  </SummaryRow>
                )}
              </tbody>
            </table>

            <div className="vp-verified-row">
              <span>
                {locale === 'vi'
                  ? `Cập nhật ${updatedLabel}`
                  : `Last updated ${updatedLabel}`}
              </span>
              <ReportLink ctx={ctx} />
            </div>
          </div>
        </div>
      </div>

      {hasContacts && <StickyCta>{stickyContacts}</StickyCta>}
    </>
  );
}

/**
 * The contact buttons render twice — inline in the summary card and again in
 * the sticky bar — so they are one component used in both places. The sticky
 * bar is narrow, so `compact` shortens the one label that does not fit.
 */
function ContactButtons({ listing, ctx, compact = false }: Props & { compact?: boolean }) {
  const { locale } = ctx;
  const website = locale === 'vi' ? 'Trang web' : 'Website';
  const call = locale === 'vi' ? 'Gọi điện' : 'Call';
  const callLabel = compact && locale === 'vi' ? 'Gọi' : call;
  const social = locale === 'vi' ? 'Mạng xã hội' : 'Social';

  return (
    <>
      {listing.website_url && (
        <a
          className="vp-btn vp-btn-primary"
          href={listing.website_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={website}
        >
          <ExternalLinkIcon />
          <span className="vp-sticky-label">{website}</span>
        </a>
      )}
      {listing.contact_email && (
        <a className="vp-btn vp-btn-email" href={`mailto:${listing.contact_email}`} aria-label="Email">
          <MailIcon />
          <span className="vp-sticky-label">Email</span>
        </a>
      )}
      {listing.contact_phone && (
        <a
          className="vp-btn vp-btn-call"
          href={`tel:${listing.contact_phone.replace(/\s/g, '')}`}
          aria-label={call}
        >
          <PhoneIcon />
          <span className="vp-sticky-label">{callLabel}</span>
        </a>
      )}
      {listing.social_media_url && (
        <a
          className="vp-btn vp-btn-social"
          href={listing.social_media_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={social}
        >
          <ChatIcon />
          <span className="vp-sticky-label">{social}</span>
        </a>
      )}
    </>
  );
}
